import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { createEscalationRuleAPI, getEscalationRulesAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import DataTableWrapper from '../../../Components/shared/DataTableWrapper';
import { companyUrl } from '../../../utils/mhNav';

export default function EscalationRules() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({
    trigger: 'WhatsApp fails',
    conditionAttempts: 2,
    fallbackAction: 'Send Email',
    delayMinutes: 15,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getEscalationRulesAPI();
      setRows(res.data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const cycleWarn =
    form.fallbackAction === 'Send Email' && form.trigger === 'Email fails';

  const columns = useMemo(
    () => [
      { name: 'Trigger', selector: (r) => r.trigger },
      { name: 'Condition', selector: (r) => `After ${r.conditionAttempts} attempts` },
      { name: 'Fallback', selector: (r) => r.fallbackAction },
      { name: 'Delay (min)', selector: (r) => r.delayMinutes, width: '110px' },
      { name: 'Status', selector: (r) => r.status },
    ],
    []
  );

  if (error && !rows.length) {
    return (
      <div>
        <PageHeader title="Escalation rules" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Notifications', path: companyUrl('notifications') }]} />
        <Button onClick={load}>Retry</Button>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Escalation rules"
        breadcrumbs={[
          { label: 'Company', path: companyUrl('dashboard') },
          { label: 'Notifications', path: companyUrl('notifications') },
          { label: 'Escalation' },
        ]}
        primaryAction={{ label: 'Add Rule', onClick: () => setModal(true) }}
      />
      {loading && !rows.length ? (
        <LoadingSkeleton rows={4} cols={4} />
      ) : (
        <DataTableWrapper columns={columns} data={rows} loading={loading} showSearch={false} />
      )}

      <Modal show={modal} onHide={() => setModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>New escalation rule</Modal.Title></Modal.Header>
        <Modal.Body>
          {cycleWarn && (
            <Alert variant="warning">This combination may loop — review carefully.</Alert>
          )}
          <Form.Group className="mb-2">
            <Form.Label>Trigger</Form.Label>
            <Form.Select value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value })}>
              <option>WhatsApp fails</option>
              <option>Email fails</option>
              <option>Teams fails</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>After N attempts</Form.Label>
            <Form.Control type="number" min={1} value={form.conditionAttempts} onChange={(e) => setForm({ ...form, conditionAttempts: Number(e.target.value) })} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Fallback action</Form.Label>
            <Form.Select value={form.fallbackAction} onChange={(e) => setForm({ ...form, fallbackAction: e.target.value })}>
              <option>Send Email</option>
              <option>Send SMS</option>
              <option>Notify Admin</option>
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Delay (minutes)</Form.Label>
            <Form.Control type="number" min={0} value={form.delayMinutes} onChange={(e) => setForm({ ...form, delayMinutes: Number(e.target.value) })} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setModal(false)}>Cancel</Button>
          <Button
            onClick={() =>
              createEscalationRuleAPI(form).then(() => {
                toast.success('Created');
                setModal(false);
                load();
              })
            }
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
